import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, addDays, startOfWeek, isSameDay, parseISO } from 'date-fns';
import { tutorAPI, bookingAPI, paymentAPI, subjectsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  ArrowLeft,
  Loader2,
  Calendar,
  Clock,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Calculator
} from 'lucide-react';
import { PricingCalculator } from '../components/PricingCalculator';

const BookingPage = () => {
  const { tutorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [availability, setAvailability] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Booking state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [duration, setDuration] = useState('60');
  const [notes, setNotes] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  
  // Package pricing state
  const [studentLevel, setStudentLevel] = useState('highSchool');
  const [packageHours, setPackageHours] = useState(5);
  const [calculatedPrice, setCalculatedPrice] = useState(null);

  useEffect(() => {
    loadTutor();
    loadSubjects();
  }, [tutorId]);

  useEffect(() => {
    if (selectedDate) {
      loadAvailability(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const loadTutor = async () => {
    try {
      const response = await tutorAPI.get(tutorId);
      setTutor(response.data);
      if (response.data.subjects?.length > 0) {
        setSelectedSubject(response.data.subjects[0]);
      }
    } catch (error) {
      toast.error('Tutor not found');
      navigate('/tutors');
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async () => {
    try {
      const response = await subjectsAPI.list();
      setSubjects(response.data.subjects);
    } catch (error) {
      console.error('Error loading subjects:', error);
    }
  };

  const loadAvailability = async (date) => {
    setLoadingSlots(true);
    try {
      const response = await tutorAPI.getAvailability(tutorId, date);
      setAvailability(prev => ({
        ...prev,
        [date]: response.data.available_slots || []
      }));
    } catch (error) {
      console.error('Error loading availability:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const weekDays = [...Array(7)].map((_, i) => addDays(weekStart, i));

  const handlePrevWeek = () => {
    setWeekStart(prev => addDays(prev, -7));
  };

  const handleNextWeek = () => {
    setWeekStart(prev => addDays(prev, 7));
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime || !selectedSubject) {
      toast.error('Please select date, time, and subject');
      return;
    }

    setBookingLoading(true);
    try {
      // Create booking
      const bookingResponse = await bookingAPI.create({
        tutor_id: tutorId,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedTime,
        duration: parseInt(duration),
        subject: selectedSubject,
        notes: notes || null
      });

      // Create payment checkout
      const paymentResponse = await paymentAPI.createCheckout({
        booking_id: bookingResponse.data.booking_id,
        origin_url: window.location.origin
      });

      // Redirect to Stripe
      window.location.href = paymentResponse.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
      setBookingLoading(false);
    }
  };

  const totalPrice = calculatedPrice ? calculatedPrice.totalCost : (tutor ? (tutor.hourly_rate * parseInt(duration)) / 60 : 0);

  // Handle price change from calculator
  const handlePriceChange = (priceData) => {
    setCalculatedPrice(priceData);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-700" />
      </div>
    );
  }

  if (!tutor) return null;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="booking-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/tutors/${tutorId}`)} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <GraduationCap className="h-8 w-8 text-indigo-700" />
              <span className="font-heading text-xl font-semibold text-slate-900">
                Book Session
              </span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar & Time Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tutor Info */}
            <Card className="border-stone-100">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={tutor.picture} alt={tutor.name} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xl">
                      {tutor.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h2 className="font-heading text-xl font-semibold text-slate-900">
                      {tutor.name}
                    </h2>
                    <p className="text-slate-600">
                      ${tutor.hourly_rate}/hour
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Date Selection */}
            <Card className="border-stone-100">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-indigo-700" />
                    Select Date
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={handlePrevWeek} data-testid="prev-week-btn">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
                    </span>
                    <Button variant="ghost" size="icon" onClick={handleNextWeek} data-testid="next-week-btn">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-2">
                  {weekDays.map((day) => {
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));
                    
                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => !isPast && handleDateSelect(day)}
                        disabled={isPast}
                        className={`p-3 rounded-xl text-center transition-all ${
                          isSelected 
                            ? 'bg-indigo-700 text-white' 
                            : isPast
                            ? 'bg-stone-100 text-slate-300 cursor-not-allowed'
                            : 'bg-stone-50 hover:bg-indigo-50 text-slate-700'
                        }`}
                        data-testid={`date-${format(day, 'yyyy-MM-dd')}`}
                      >
                        <span className="text-xs block">{format(day, 'EEE')}</span>
                        <span className="text-lg font-semibold">{format(day, 'd')}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Time Selection */}
            {selectedDate && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="border-stone-100">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Clock className="h-5 w-5 text-indigo-700" />
                      Available Times for {format(selectedDate, 'MMMM d')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingSlots ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-indigo-700" />
                      </div>
                    ) : (
                      <>
                        {availability[format(selectedDate, 'yyyy-MM-dd')]?.length > 0 ? (
                          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                            {availability[format(selectedDate, 'yyyy-MM-dd')].map((time) => (
                              <button
                                key={time}
                                onClick={() => setSelectedTime(time)}
                                className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                                  selectedTime === time
                                    ? 'bg-indigo-700 text-white'
                                    : 'bg-stone-50 hover:bg-indigo-50 text-slate-700'
                                }`}
                                data-testid={`time-${time}`}
                              >
                                {time}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-slate-500 py-8">
                            No available slots for this date. Please try another date.
                          </p>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Session Details */}
            <Card className="border-stone-100">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Session Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                      <SelectTrigger data-testid="subject-select">
                        <SelectValue placeholder="Select subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {(tutor.subjects?.length > 0 ? tutor.subjects : subjects).map((subject) => (
                          <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Duration</Label>
                    <Select value={duration} onValueChange={setDuration}>
                      <SelectTrigger data-testid="duration-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="60">60 minutes</SelectItem>
                        <SelectItem value="90">90 minutes</SelectItem>
                        <SelectItem value="120">120 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea
                    placeholder="Describe what you'd like to learn or any specific topics..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    data-testid="notes-textarea"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Booking Summary */}
          <div className="space-y-6">
            {/* Pricing Calculator */}
            <PricingCalculator 
              variant="compact" 
              onPriceChange={handlePriceChange}
              initialHours={packageHours}
              initialLevel={studentLevel}
            />
            
            <Card className="border-stone-100 sticky top-24">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Tutor</span>
                    <span className="text-slate-900 font-medium">{tutor.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Date</span>
                    <span className="text-slate-900 font-medium">
                      {selectedDate ? format(selectedDate, 'MMM d, yyyy') : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Time</span>
                    <span className="text-slate-900 font-medium">
                      {selectedTime || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Subject</span>
                    <span className="text-slate-900 font-medium">{selectedSubject || '-'}</span>
                  </div>
                  {calculatedPrice && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Package</span>
                        <span className="text-slate-900 font-medium">{calculatedPrice.packageName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Hours</span>
                        <span className="text-slate-900 font-medium">{calculatedPrice.hours} hrs</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Rate</span>
                        <span className="text-slate-900 font-medium">${calculatedPrice.hourlyRate}/hr</span>
                      </div>
                      {calculatedPrice.discountPercentage > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount</span>
                          <span className="font-medium">~{calculatedPrice.discountPercentage}% off</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="border-t border-stone-100 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-900 font-semibold">Total</span>
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-5 w-5" />
                      <span className="text-2xl font-bold text-slate-900" data-testid="booking-total">
                        {calculatedPrice ? calculatedPrice.totalCost.toLocaleString() : totalPrice.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {calculatedPrice?.savingsAmount > 0 && (
                    <p className="text-xs text-green-600 text-right mt-1">
                      You save ${calculatedPrice.savingsAmount}!
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleBooking}
                  disabled={!selectedDate || !selectedTime || !selectedSubject || bookingLoading}
                  className="w-full rounded-full bg-slate-900 hover:bg-slate-800 py-6 text-lg"
                  data-testid="proceed-to-payment-btn"
                >
                  {bookingLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="h-5 w-5 mr-2" />
                  )}
                  Proceed to Payment
                </Button>

                <p className="text-xs text-center text-slate-500">
                  Secure payment powered by Stripe
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BookingPage;
